import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Appearance, type ImageSourcePropType } from 'react-native';
import { DefaultTheme, type Theme as NavTheme } from '@react-navigation/native';
import { setActiveResolvedVisualThemeId } from './activeVisualThemeRef';
import { invalidateAndroidChannels } from '../notifications/androidChannels';
import { refreshCallKeepRingtone } from '../notifications/callKeepInit';
import { chatListPalette } from './chatListPalette';
import { makeChatListStyles, type ChatListStyles } from './chatListStylesFromPalette';
import { readSavedVisualTheme, writeSavedVisualTheme } from './visualThemeStorage';
import type { ResolvedVisualThemeId, VisualThemeId } from './visualThemeTypes';
import {
  DEFAULT_VISUAL_THEME,
  isVisualThemeId,
  msUntilNextAutoBoundary,
  resolveAutoTheme,
  systemDefaultVisualTheme,
} from './visualThemeTypes';
import { SunWallpaper } from './wallpapers/SunWallpaper';
import { CosmosWallpaper } from './wallpapers/CosmosWallpaper';

const WALL_VAR: ImageSourcePropType = require('../../assets/chat-campfire-forest.jpg');

function resolveVisualTheme(id: VisualThemeId, now: Date): ResolvedVisualThemeId {
  if (id === 'auto') return resolveAutoTheme(now);
  if (id === 'sun' || id === 'cosmos' || id === 'neutral' || id === 'dark' || id === 'varangian') {
    return id;
  }
  return DEFAULT_VISUAL_THEME as ResolvedVisualThemeId;
}

type AvatarChrome = {
  /** Контур маски аватарки по умолчанию (если не передан outlineColor). */
  outline: string;
  /** Фон слота предпросмотра до раскладки кропа. */
  previewSlotBg: string;
};

type Ctx = {
  /** The user's stored preference (may be `'auto'`). Use this for the picker UI. */
  visualTheme: VisualThemeId;
  /**
   * The currently active palette after resolving `auto` against the local clock.
   * Pattern-match on this in code that needs to know "is it day mode or night
   * mode right now"; never compare against `visualTheme` directly for that.
   */
  resolvedVisualTheme: ResolvedVisualThemeId;
  setVisualTheme: (t: VisualThemeId) => void;
  ready: boolean;
  chatListStyles: ChatListStyles;
  chatListHeaderIconTint: string;
  chatListWallpaper: ImageSourcePropType | null;
  /**
   * SVG-rendered wallpaper used by themes that don't ship a bitmap asset (sun,
   * cosmos). Consumers should accept dimensions and render this on the same
   * z-order as `chatListWallpaper`.
   */
  chatListWallpaperRenderer: ((width: number, height: number) => React.ReactNode) | null;
  navigationTheme: NavTheme;
  loginChrome: {
    rootBg: string;
    cardBg: string;
    titleColor: string;
    subColor: string;
    epigraphColor: string;
    inputBg: string;
    inputBorder: string;
    inputText: string;
    btnBg: string;
    serifTitle: boolean;
  };
  avatarChrome: AvatarChrome;
};

const VisualThemeContext = createContext<Ctx | null>(null);

function buildAvatarChrome(id: ResolvedVisualThemeId): AvatarChrome {
  if (id === 'neutral' || id === 'sun') {
    return {
      outline: 'rgba(15, 23, 42, 0.62)',
      previewSlotBg: id === 'sun' ? '#fef3c7' : '#f1f5f9',
    };
  }
  if (id === 'dark' || id === 'cosmos') {
    return {
      outline: 'rgba(250, 250, 250, 0.48)',
      previewSlotBg: id === 'cosmos' ? '#1e1b4b' : '#27272a',
    };
  }
  return {
    outline: 'rgba(248, 250, 252, 0.52)',
    previewSlotBg: '#334155',
  };
}

function buildNavTheme(id: ResolvedVisualThemeId): NavTheme {
  if (id === 'neutral') {
    return {
      ...DefaultTheme,
      colors: {
        ...DefaultTheme.colors,
        background: '#e7edf5',
        card: '#ffffff',
        text: '#0f172a',
        border: '#94a3b8',
        primary: '#0369a1',
      },
    };
  }
  if (id === 'sun') {
    return {
      ...DefaultTheme,
      colors: {
        ...DefaultTheme.colors,
        background: '#fde68a',
        card: '#fffbeb',
        text: '#431407',
        border: '#b45309',
        primary: '#b45309',
      },
    };
  }
  if (id === 'dark') {
    return {
      ...DefaultTheme,
      dark: true,
      colors: {
        ...DefaultTheme.colors,
        background: '#09090b',
        card: '#09090b',
        text: '#fafafa',
        border: '#27272a',
        primary: '#3b82f6',
      },
    };
  }
  if (id === 'cosmos') {
    return {
      ...DefaultTheme,
      dark: true,
      colors: {
        ...DefaultTheme.colors,
        background: '#020617',
        card: '#0f172a',
        text: '#f8fafc',
        border: '#312e81',
        primary: '#818cf8',
      },
    };
  }
  return {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: '#0c1924',
      card: '#0c1924',
      text: '#f8fafc',
      border: '#334155',
      primary: '#2563eb',
    },
  };
}

function buildLoginChrome(id: ResolvedVisualThemeId) {
  if (id === 'neutral') {
    return {
      rootBg: '#e7edf5',
      cardBg: '#ffffff',
      titleColor: '#0f172a',
      subColor: '#475569',
      epigraphColor: '#334155',
      inputBg: '#f8fafc',
      inputBorder: '#94a3b8',
      inputText: '#0f172a',
      btnBg: '#0369a1',
      serifTitle: false,
    };
  }
  if (id === 'sun') {
    return {
      rootBg: '#fde68a',
      cardBg: '#fffbeb',
      titleColor: '#431407',
      subColor: '#92400e',
      epigraphColor: '#7c2d12',
      inputBg: '#fef3c7',
      inputBorder: '#b45309',
      inputText: '#431407',
      btnBg: '#b45309',
      serifTitle: false,
    };
  }
  if (id === 'dark') {
    return {
      rootBg: '#09090b',
      cardBg: '#18181b',
      titleColor: '#fafafa',
      subColor: '#a1a1aa',
      epigraphColor: '#d4d4d8',
      inputBg: '#09090b',
      inputBorder: '#3f3f46',
      inputText: '#fafafa',
      btnBg: '#2563eb',
      serifTitle: false,
    };
  }
  if (id === 'cosmos') {
    return {
      rootBg: '#020617',
      cardBg: '#0f172a',
      titleColor: '#f8fafc',
      subColor: '#a5b4fc',
      epigraphColor: '#c7d2fe',
      inputBg: '#0f172a',
      inputBorder: '#312e81',
      inputText: '#f8fafc',
      btnBg: '#4f46e5',
      serifTitle: true,
    };
  }
  return {
    rootBg: '#0f172a',
    cardBg: '#1e293b',
    titleColor: '#f5edd8',
    subColor: '#94a3b8',
    epigraphColor: '#d6c9a8',
    inputBg: '#0f172a',
    inputBorder: '#334155',
    inputText: '#f8fafc',
    btnBg: '#2563eb',
    serifTitle: true,
  };
}

export function VisualThemeProvider({ children }: { children: React.ReactNode }) {
  const [visualTheme, setVisualThemeState] = useState<VisualThemeId>(DEFAULT_VISUAL_THEME);
  const [ready, setReady] = useState(false);
  /**
   * Wall-clock moment the `auto` preset is resolved against. Refreshed together
   * with every theme change and by the day/night boundary timer, so resolving
   * stays a pure function of state instead of reading the clock during render.
   */
  const [autoNow, setAutoNow] = useState(() => new Date());

  const [fadeAnim] = useState(() => new Animated.Value(1));
  const prevResolvedTheme = useRef<ResolvedVisualThemeId | null>(null);
  const isFirstLoad = useRef(true);

  /**
   * Выбирал ли пользователь тему сам. Пока нет — стартовое значение берётся у системы
   * и следует за ней вживую (DS-1); первый же ручной выбор эту связь обрывает
   * навсегда, иначе смена схемы телефона перебивала бы решение человека.
   */
  const userPickedThemeRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const raw = await readSavedVisualTheme();
      const saved = isVisualThemeId(raw) ? raw : null;
      userPickedThemeRef.current = saved != null;
      const next = saved ?? systemDefaultVisualTheme(Appearance.getColorScheme());
      if (!cancelled) {
        setVisualThemeState(next);
        setAutoNow(new Date());
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Телефон переключили на тёмную схему, а своей темы у пользователя ещё нет —
  // приложение обязано поехать следом. После ручного выбора подписка молчит.
  useEffect(() => {
    const sub = Appearance.addChangeListener(({ colorScheme }) => {
      if (userPickedThemeRef.current) return;
      const next = systemDefaultVisualTheme(colorScheme);
      setVisualThemeState(next);
      setAutoNow(new Date());
    });
    return () => sub.remove();
  }, []);

  // Only the `auto` preset needs a wake-up timer — fixed themes don't change.
  // We schedule a single timeout aimed at the next day/night boundary instead
  // of polling per minute, then chain another one inside the handler.
  useEffect(() => {
    if (visualTheme !== 'auto') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (cancelled) return;
      const delay = msUntilNextAutoBoundary(new Date());
      timer = setTimeout(() => {
        if (cancelled) return;
        setAutoNow(new Date());
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [visualTheme]);

  const setVisualTheme = useCallback((t: VisualThemeId) => {
    userPickedThemeRef.current = true;
    setVisualThemeState(t);
    setAutoNow(new Date());
    void writeSavedVisualTheme(t);
  }, []);

  // `autoNow` moves whenever the boundary timer fires or the theme changes, so the
  // resolved theme refreshes at the same moments as before, without reading the
  // clock during render.
  const resolvedVisualTheme = useMemo<ResolvedVisualThemeId>(
    () => resolveVisualTheme(visualTheme, autoNow),
    [visualTheme, autoNow],
  );

  // Keep the resolved-theme module ref in sync so sound helpers can always
  // query the actual active palette without going through React context.
  useEffect(() => {
    setActiveResolvedVisualThemeId(resolvedVisualTheme);
    /**
     * Тема сменилась — значит сменился и набор звуков, а два звуковых канала живут вне
     * приложения и сами об этом не узнают:
     *
     * <ul>
     *   <li><b>Каналы уведомлений Android.</b> Звук канала нельзя перенастроить после
     *       создания, поэтому у каждого набора свой id; сбрасываем память о заведённых
     *       каналах, и следующее уведомление создаст нужный, попутно убрав прежний.
     *   <li><b>Рингтон CallKit на iOS.</b> Он задаётся один раз при настройке провайдера,
     *       и без повторного `setup()` входящий звонил бы звуком прошлой темы.
     * </ul>
     */
    invalidateAndroidChannels();
    void refreshCallKeepRingtone();
  }, [resolvedVisualTheme]);

  // Smooth cross-fade when the resolved palette changes (manual switch or
  // auto day/night boundary crossing). We skip the very first render so the
  // app doesn't fade in from blank on launch.
  useEffect(() => {
    if (!ready) return;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      prevResolvedTheme.current = resolvedVisualTheme;
      return;
    }
    if (prevResolvedTheme.current === resolvedVisualTheme) return;
    prevResolvedTheme.current = resolvedVisualTheme;
    fadeAnim.setValue(0);
    Animated.timing(fadeAnim, { toValue: 1, duration: 280, useNativeDriver: true }).start();
  }, [resolvedVisualTheme, ready, fadeAnim]);

  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);
  const chatListStyles = useMemo(() => makeChatListStyles(pal), [pal]);
  const navigationTheme = useMemo(() => buildNavTheme(resolvedVisualTheme), [resolvedVisualTheme]);
  const loginChrome = useMemo(() => buildLoginChrome(resolvedVisualTheme), [resolvedVisualTheme]);
  const avatarChrome = useMemo(() => buildAvatarChrome(resolvedVisualTheme), [resolvedVisualTheme]);

  const chatListWallpaper = resolvedVisualTheme === 'varangian' ? WALL_VAR : null;
  const chatListWallpaperRenderer = useMemo<
    ((width: number, height: number) => React.ReactNode) | null
  >(() => {
    if (resolvedVisualTheme === 'sun') {
      return (width, height) => <SunWallpaper width={width} height={height} />;
    }
    if (resolvedVisualTheme === 'cosmos') {
      return (width, height) => <CosmosWallpaper width={width} height={height} />;
    }
    return null;
  }, [resolvedVisualTheme]);

  const value = useMemo(
    () => ({
      visualTheme,
      resolvedVisualTheme,
      setVisualTheme,
      ready,
      chatListStyles,
      chatListHeaderIconTint: pal.headerIconTint,
      chatListWallpaper,
      chatListWallpaperRenderer,
      navigationTheme,
      loginChrome,
      avatarChrome,
    }),
    [
      visualTheme,
      resolvedVisualTheme,
      setVisualTheme,
      ready,
      chatListStyles,
      pal.headerIconTint,
      chatListWallpaper,
      chatListWallpaperRenderer,
      navigationTheme,
      loginChrome,
      avatarChrome,
    ],
  );

  return (
    <VisualThemeContext.Provider value={value}>
      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        {children}
      </Animated.View>
    </VisualThemeContext.Provider>
  );
}

export function useVisualTheme(): Ctx {
  const v = useContext(VisualThemeContext);
  if (!v) throw new Error('useVisualTheme must be used within VisualThemeProvider');
  return v;
}
